import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, User, AlertCircle } from 'lucide-react';

/**
 * Validate a `returnTo` query parameter against open-redirect attacks.
 * Only allow relative internal dispatch paths beginning with "/dispatch/".
 */
function safeReturnTo(raw) {
  if (!raw || typeof raw !== 'string') return '/dispatch/dashboard';
  let value = raw;
  try { value = decodeURIComponent(raw); } catch { /* fall through */ }
  if (
    value.startsWith('//') ||
    value.startsWith('\\') ||
    /^[a-z][a-z0-9+.-]*:/i.test(value) ||
    !value.startsWith('/dispatch/')
  ) {
    return '/dispatch/dashboard';
  }
  if (value.includes('..')) return '/dispatch/dashboard';
  return value;
}

const DispatchLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return safeReturnTo(params.get('returnTo'));
  }, [location.search]);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Sign-in failed');
      }

      // Session cookie is set by the server. If a password reset is required,
      // route the user through the reset flow instead of the dashboard.
      if (data && data.must_reset) {
        navigate('/dispatch/change-password?first_login=1', { replace: true });
        return;
      }
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#004C97] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">MoveraCare Mobility</h1>
          <p className="text-[#F9A825] text-lg font-semibold">Dispatch Portal</p>
        </div>

        <Card className="border-2 border-[#F9A825]/20 shadow-2xl" data-testid="dispatch-login-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center text-[#004C97]">Sign In</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access the dispatch portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="dispatch-login-form">
              {error && (
                <div
                  className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2"
                  data-testid="dispatch-login-error"
                >
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700" htmlFor="dispatch-login-username">
                  Username or Email
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="dispatch-login-username"
                    data-testid="dispatch-login-username"
                    type="text"
                    required
                    autoComplete="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
                    placeholder="Enter username or email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700" htmlFor="dispatch-login-password">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="dispatch-login-password"
                    data-testid="dispatch-login-password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
                    placeholder="Enter password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="dispatch-login-submit"
                className="w-full bg-[#F9A825] hover:bg-[#E08E1E] text-white font-semibold py-2"
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200 flex justify-center">
              <Link
                to="/dispatch/forgot-password"
                data-testid="dispatch-forgot-password-link"
                className="text-sm text-[#004C97] hover:underline font-medium"
              >
                Forgot your password?
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-200">
            © 2025 MoveraCare Mobility. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DispatchLogin;
